/**
 * Human names for permissions, for any screen that has to mention one.
 *
 * A screen that refuses somebody names what they would need — and a dotted
 * key ("system.administer") is not a name: the person reading it cannot tell
 * their administrator what to grant, and the administrator's role editor
 * never shows the key either. Every refusal uses these words instead.
 *
 * Two vocabularies, as in the rest of the platform: the server's own keys,
 * which the roles editor ticks one checkbox per key (kept identical to the
 * API's `PERMISSION_LABELS` — its catalog test compares them), and the
 * coarse menu names the web console gates navigation with.
 */

export const SERVER_PERMISSION_LABELS: Record<string, string> = {
  'employees.view': 'View employee directory',
  'employees.view_sensitive': 'View sensitive employee details',
  'employees.create': 'Add employees',
  'employees.edit': 'Edit employee records',
  'employees.change_status': 'Change employment status',
  'employees.export': 'Export employee data',

  'attendance.view': 'View attendance records',
  'attendance.view_team': 'View team attendance',
  'attendance.view_security_evidence': 'View attendance evidence',
  'attendance.review': 'Review flagged attendance',
  'attendance.correction.approve': 'Approve attendance corrections',
  'attendance.lock': 'Lock timesheet periods',
  'attendance.export': 'Export attendance',

  'schedules.view': 'View schedules',
  'schedules.create': 'Create shifts and rosters',
  'schedules.assign': 'Assign shifts',
  'schedules.publish': 'Publish schedules',

  'leave.view': 'View leave requests',
  'leave.approve_manager': 'Approve leave (manager step)',
  'leave.approve_hr': 'Approve leave (HR step)',
  'leave.adjust_balance': 'Adjust leave balances',
  'leave.cancel_approved': 'Cancel approved leave',
  'leave.configure': 'Configure leave types',
  'leave.export': 'Export leave data',

  'overtime.view': 'View overtime requests',
  'overtime.approve_manager': 'Approve overtime (manager step)',
  'overtime.approve_hr': 'Approve overtime (HR step)',
  'overtime.mark_payroll_ready': 'Send approved overtime to payroll',

  'payroll.view': 'View pay runs',
  'payroll.view_sensitive': 'View individual pay',
  'payroll.create_period': 'Open pay periods',
  'payroll.import_inputs': 'Import payroll inputs',
  'payroll.calculate': 'Calculate payroll',
  'payroll.submit': 'Submit pay runs for approval',
  'payroll.approve': 'Approve pay runs',
  'payroll.lock': 'Lock pay runs',
  'payroll.reopen': 'Reopen locked pay runs',
  'payroll.publish_payslips': 'Publish payslips',
  'payroll.configure': 'Configure pay groups and statutory tables',

  'compensation.view': 'View salary history',
  'compensation.manage': 'Record salary changes',

  'employee_relations.view': 'View case register',
  'employee_relations.view_confidential': 'Read case substance',
  'employee_relations.create': 'Open cases',
  'employee_relations.edit': 'Record case progress',

  'recruitment.view': 'View requisitions and candidates',
  'recruitment.create_requisition': 'Raise job requisitions',
  'recruitment.approve_requisition': 'Approve job requisitions',
  'recruitment.manage_candidates': 'Manage candidates',
  'recruitment.schedule_interview': 'Schedule interviews',
  'recruitment.create_offer': 'Extend offers',
  'recruitment.convert_candidate': 'Convert candidates to employees',

  'document.manage': 'Manage employee documents',
  'policy.publish': 'Publish policies',
  'announcement.publish': 'Publish announcements',
  'asset.manage': 'Manage company assets',
  'learning.manage': 'Manage training',

  'organization.manage': 'Manage organisation structure',
  'geofence.manage': 'Manage work locations and geofences',

  'approvals.view': 'View own approval queue',
  'approvals.view_all': 'View every open approval',
  'settings.configure': 'Change company settings',
  'users.view': 'View user accounts',
  'users.manage': 'Manage user accounts',
  'users.deactivate': 'Deactivate user accounts',
  'users.manage_roles': 'Manage roles and role assignments',
  'report.view': 'View reports',
  'audit.view': 'View audit log',
  'audit.view_sensitive': 'View sensitive audit entries',
  'audit.export': 'Export audit log',
};

/** The coarse menu names, where the role-editor groups do not already name them. */
const COARSE_PERMISSION_LABELS: Record<string, string> = {
  'approval.view': 'View approvals',
  'approval.decide': 'Decide approvals',
  'approval.return': 'Return approvals for changes',
  'approval.delegate': 'Delegate approvals',
  'team.view': 'View own team',
  'team.attendance.view': 'View team attendance',
  'team.schedule.view': 'View team schedules',
  'team.leave_calendar.view': 'View team leave calendar',
  'department.view': 'View own department',
  'department.report.view': 'View department reports',
  'employee.view_all': 'View all employees',
  'employee.manage': 'Manage employees',
  'work_location.manage': 'Manage work locations',
  'attendance_policy.manage': 'Manage attendance policy',
  'attendance.approve': 'Approve attendance',
  'attendance.create_adjustment': 'Create attendance adjustments',
  'shift.manage': 'Manage shifts',
  'schedule.assign': 'Assign schedules',
  'leave.balance.manage': 'Manage leave balances',
  'leave.type.manage': 'Manage leave types',
  'payroll.run': 'Run payroll',
  'payslip.publish': 'Publish payslips',
  'statutory_table.manage': 'Manage statutory tables',
  'recruitment.manage': 'Manage recruitment',
  'audit_log.view': 'View audit log',
  'system.administer': 'Administer users, roles and company settings',
  'onboarding.manage': 'Manage onboarding',
  'probation.decide': 'Decide probation',
  'performance.manage': 'Manage performance reviews',
  'offboarding.manage': 'Manage offboarding',
  'benefits.view': 'View benefits',
  'benefits.manage': 'Manage benefits',
  'loans.view': 'View loans',
  'loans.manage': 'Manage loans',
};

/** "leave.balance.manage" → "Leave balance manage": words, never a code. */
function humanisePermission(key: string): string {
  const words = key.replace(/[._]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The name to show for a permission, in either vocabulary.
 *
 * Unknown keys still come back as words rather than a dotted code.
 */
export function permissionLabel(key: string): string {
  return SERVER_PERMISSION_LABELS[key] ?? COARSE_PERMISSION_LABELS[key] ?? humanisePermission(key);
}
